import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { ReactSession } from "../../../lib/secure_reactsession.js";
import toast from "react-hot-toast";
import { getTestData } from "../../../services/test";
import { putTestSurveyQuestions } from "../../../services/researcher_test_survey.js";
import {
    surveyQuestionsToPutPayload,
    extractOptionLabelsFromQuestion,
    remapSurveyQuestionJumpTargetsToIds,
    getDefaultForwardJumpTarget,
    collectSurveyJumpRulesTargetingIndex,
    applySurveyQuestionDeleteWithJumpResets,
    remapSurveyJumpTargetsAfterReorder,
    findBackwardJumpViolations,
    fixBackwardJumpsInSurvey,
    isBackwardOrSameJump,
    surveyJumpGraphHasCycleFromStart,
    getSingleChoiceJumpTarget,
    resolveJumpTargetDisplayNumber,
} from "../../../lib/surveyPutPayload.js";
import { settings } from "../../../data/settings.js";
import { renderTestTitleCreate, copyToClipboard, showToast } from "../../../lib/helpers.js";
import { LayoutResearcher } from "../../themes/userq/Layouts/layout_researcher";
import { Button } from "../../themes/userq/Button/Button";
import { LoadingIcon } from "../../loader/loadingIconNew";
import { Text } from "../../themes/userq/Text/Text";
import { Icon } from "../../themes/userq/Icon/Icon";
import ConfirmationModal from "../../themes/userq/Modal/ConfirmationModal";
import HelpModal from "../../themes/userq/Modal/HelpModal.js";
import { Helmet } from "react-helmet";
import { getPageTitle } from "../../../lib/pageTitle";
import { Stepper } from "../../themes/userq/Stepper/Stepper";
import { Accordian } from "../../themes/userq/Accordion/Accordion";
import { useTestBuilderData } from "./contexts/TestBuilderContext.js";
import { Question } from "../../themes/userq/Question/Question";
import { QuestionCard } from "../../themes/userq/QuestionCard/QuestionCard";
import { QuestionInput } from "../../themes/userq/Question/QuestionInput";
import { ToastMessage } from "../../themes/userq/ToastMessage/ToastMessage";
import Header from "./components/header";
import redirectToStep from "./redirect_to_step";
import { IntroductionQuestions } from "./introduction_questions.js";
import { ConclusionQuestions } from "./conclusion_questions";
import { GuideLink } from "../../themes/userq/GuideLink/GuideLink.js";
import AIModal from "../../themes/userq/AIModal/AIModal";
import SurveyCreatorAI from "./survey/survey_creator_ai.js";
import "./survey/survey_creator_ai.css";
import cloneDeep from "lodash/cloneDeep";

/** `id` = 1-based sequence in list order; `_surveyQuestionPk` = DB row id for PUT when known. */
function normalizeSurveyQuestionSequentialIds(questions) {
    if (!Array.isArray(questions)) {
        return [];
    }
    return questions.map((q, index) => ({
        ...q,
        id: index + 1,
    }));
}

/** Remove ids assigned before insert; `normalizeSurveyQuestionSequentialIds` sets order `id`. */
function stripQuestionTransientIds(question) {
    if (!question || typeof question !== "object") {
        return question;
    }
    const o = { ...question };
    delete o.id;
    delete o.questionid;
    if (o.__surveyClientKey == null || o.__surveyClientKey === "") {
        o.__surveyClientKey =
            typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
                ? crypto.randomUUID()
                : `sq-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    }
    return o;
}

/** Stable React / DnD id: DB pk when known, else client-only key for rows not yet persisted. */
function getSurveyQuestionStableKey(question, index) {
    if (question?._surveyQuestionPk != null && question._surveyQuestionPk !== "") {
        return `sq-pk-${question._surveyQuestionPk}`;
    }
    if (question?.__surveyClientKey != null && question.__surveyClientKey !== "") {
        return `sq-ck-${question.__surveyClientKey}`;
    }
    return `sq-i-${index}`;
}

function ensureSurveyQuestionClientKey(q) {
    if (!q || typeof q !== "object") {
        return q;
    }
    if (q._surveyQuestionPk != null && q._surveyQuestionPk !== "") {
        return q;
    }
    if (q.__surveyClientKey != null && q.__surveyClientKey !== "") {
        return q;
    }
    return {
        ...q,
        __surveyClientKey:
            typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
                ? crypto.randomUUID()
                : `sq-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
    };
}

/** Stable key used to track per-question warning state across re-renders and refreshes. */
function getQuestionWarningKey(q) {
    if (!q || typeof q !== "object") return null;
    if (q._surveyQuestionPk != null && q._surveyQuestionPk !== "") return `pk-${q._surveyQuestionPk}`;
    if (q.__surveyClientKey != null && q.__surveyClientKey !== "") return `ck-${q.__surveyClientKey}`;
    return null;
}

/** Collect "Q{from} → Q{to}" strings for violations in a proposed question list. */
function buildViolationRulesText(violations, questions) {
    const seen = new Set();
    for (const v of violations) {
        const q = questions[v.parentIndex];
        if (!q) continue;
        const from = v.parentIndex + 1;
        let jumpValue;
        if (q.questionType === "singlechoice" && v.optionIndex !== null) {
            jumpValue = getSingleChoiceJumpTarget(q, v.optionIndex);
        } else {
            jumpValue = q.jump_to_question != null && q.jump_to_question !== ""
                ? q.jump_to_question
                : q.jumpTo;
        }
        const toNum = resolveJumpTargetDisplayNumber(jumpValue, questions);
        seen.add(toNum != null ? `Q${from} → Q${toNum}` : `Q${from}`);
    }
    return [...seen].join(", ");
}

/**
 * Build the in-memory question list from API payload (`surveyQuestionSplitApi` or legacy `surveyQuestion`).
 * Always assigns `id` = 1-based list order; persisted row key in `_surveyQuestionPk` when from split API.
 */
function parseSurveyQuestionsFromTest(test) {
    if (!test) {
        return [];
    }
    const questions = [];

    const split = test.surveyQuestionSplitApi;
    if (Array.isArray(split) && split.length > 0) {
        split.forEach((questionData) => {
            try {
                const parsed = JSON.parse(questionData.questions);
                delete parsed.id;
                delete parsed.questionid;
                parsed._surveyQuestionPk = questionData.id;
                parsed.is_ai_generated = questionData.is_ai_generated;
                questions.push(parsed);
            } catch (e) {
                console.warn("Invalid survey row JSON", questionData?.id, e);
            }
        });
        return remapSurveyQuestionJumpTargetsToIds(
            normalizeSurveyQuestionSequentialIds(questions.map(ensureSurveyQuestionClientKey)),
        );
    }

    const legacy = test.surveyQuestion;
    if (Array.isArray(legacy) && legacy.length > 0) {
        legacy.forEach((questionJson, index) => {
            try {
                const parsed =
                    typeof questionJson === "string" ? JSON.parse(questionJson) : questionJson;
                delete parsed.id;
                delete parsed.questionid;
                questions.push(parsed);
            } catch (e) {
                console.warn("Invalid legacy survey JSON", index, e);
            }
        });
    }

    return remapSurveyQuestionJumpTargetsToIds(
        normalizeSurveyQuestionSequentialIds(questions.map(ensureSurveyQuestionClientKey)),
    );
}

/**
 * Survey step — fresh baseline: load test + questions from API and refresh on demand.
 * Rebuild builder UX on top of `refreshSurvey` / `surveyQuestions` / `test` as needed.
 */
export default function Survey() {
    const { test_id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    ReactSession.setStoreType("localStorage");

    const { getTestLength, allowEditAccess } = useTestBuilderData();
    const allowEditAccessRef = useRef(allowEditAccess);
    const getTestLengthRef = useRef(getTestLength);
    allowEditAccessRef.current = allowEditAccess;
    getTestLengthRef.current = getTestLength;

    const [test, setTest] = useState("");
    const [isSaved, setIsSaved] = useState({ status: "", message: "" });
    const [show_support_panel, setShowSupportPanel] = useState(false);
    const [surveyQuestions, setSurveyQuestions] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [stepperError, setStepperError] = useState(false);
    const [surveyError, setSurveyError] = useState(null);

    const [accordians, setAccordians] = useState({
        introduction: false,
        survey: searchParams.get("error") === "true",
        conclusion: false,
    });
    const [introCount, setIntroCount] = useState(0);
    const [concCount, setConcCount] = useState(0);
    const [introductionQuestions, setIntroductionQuestions] = useState(null);
    const [conclusionQuestions, setConclusionQuestions] = useState(null);

    const introductionQuestionRef = useRef(null);
    const conclusionQuestionRef = useRef(null);

    /** "manual" = question list; "ai" = Survey creator (AI) flow */
    const [surveyViewMode, setSurveyViewMode] = useState("manual");
    const [showAiSurveyModal, setShowAiSurveyModal] = useState(false);
    const [aiModalType, setAiModalType] = useState("generate");
    const [surveyGoalModal, setSurveyGoalModal] = useState({ open: false });
    const [questionType, setQuestionType] = useState("");
    /** Index into `surveyQuestions` when editing an existing row; null when adding. */
    const [editingSurveyIndex, setEditingSurveyIndex] = useState(null);
    const questionInputValidateRef = useRef(null);
    /** Pending survey row index when delete confirmation modal is open (avoids stale closure). */
    const pendingDeleteSurveyIndexRef = useRef(null);
    /** True when delete modal was opened because other questions had jumps targeting the deleted row. */
    const pendingDeleteHadJumpRulesRef = useRef(false);
    /** Proposed list after drag reorder when backward-jump confirmation is pending. */
    const pendingSurveyReorderRef = useRef(null);
    /**
     * Pending jump change when a backward/cycle violation confirmation modal is open.
     * Shape: { next: Array, questionIndex: number, optionIndex: number|null, isSingleChoice: boolean }
     */
    const pendingJumpUpdateRef = useRef(null);
    /** Stable keys of questions whose jump logic was auto-reset (shows warning icon). */
    const pendingDeleteAffectedParentsRef = useRef([]);

    const [jumpWarningKeys, setJumpWarningKeys] = useState(new Set());

    const [deleteQuestionConfirmModal, setDeleteQuestionConfirmModal] = useState({
        open: false,
        confirm_title: "",
        confirm_message: "",
        cancel_btn_title: "",
        confirm_btn_title: "",
    });

    const [reorderLogicConfirmModal, setReorderLogicConfirmModal] = useState({
        open: false,
        confirm_title: "",
        confirm_message: "",
        cancel_btn_title: "",
        confirm_btn_title: "",
    });

    const [jumpLogicViolationModal, setJumpLogicViolationModal] = useState({
        open: false,
        confirm_title: "",
        confirm_message: "",
        cancel_btn_title: "",
        confirm_btn_title: "",
    });

    const refreshSurvey = useCallback(async (options = {}) => {
        const silent = options.silent === true;
        if (!silent) {
            setLoading(true);
        }
        setLoadError(null);
        const token = ReactSession.get("token");

        try {
            const response = await getTestData({ test_id }, token);

            if (!response?.success) {
                setLoadError(response?.message || "Could not load test.");
                setTest("");
                setSurveyQuestions([]);
                setIntroductionQuestions(null);
                setConclusionQuestions(null);
                setIntroCount(0);
                setConcCount(0);
                return;
            }

            const loadedTest = response.test;

            if (loadedTest.workspace_project && response.workspace_project_team_member) {
                if (response.workspace_project_team_member.status !== "active") {
                    navigate(
                        "/wsp/" +
                        loadedTest.workspace_project.workspace_id +
                        "/p/" +
                        loadedTest.workspace_project.id,
                    );
                    return;
                }
            }
            if (loadedTest.status === "published") {
                navigate("/wsp/" + loadedTest.workspace_id + "/p/" + loadedTest.workspace_project_id);
                return;
            }

            allowEditAccessRef.current({
                test_id,
                workspace_id: loadedTest.workspace_id,
                workspace_project_id: loadedTest.workspace_project_id,
            });
            getTestLengthRef.current(test_id);

            ReactSession.set("methodology", loadedTest.methodology);
            redirectToStep({
                test_id,
                current_step: 2,
                saved_step: loadedTest.saved_step,
                navigate,
                test: loadedTest,
            });

            setTest(loadedTest);
            setSurveyQuestions(parseSurveyQuestionsFromTest(loadedTest));

            const briefing = loadedTest.briefingQuestion || [];
            const debriefing = loadedTest.debriefingQuestion || [];
            setIntroCount(briefing.length);
            setConcCount(debriefing.length);
            setIntroductionQuestions(briefing);
            setConclusionQuestions(debriefing);
        } catch (e) {
            setLoadError(e?.message || "Something went wrong while loading.");
            setTest("");
            setSurveyQuestions([]);
            setIntroductionQuestions(null);
            setConclusionQuestions(null);
            setIntroCount(0);
            setConcCount(0);
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    }, [test_id, navigate]);

    const switchSurveyAiToManual = useCallback(
        (fromSuccess) => {
            if (fromSuccess) {
                refreshSurvey({ silent: true });
            }
            setSurveyViewMode("manual");
        },
        [refreshSurvey],
    );

    useEffect(() => {
        refreshSurvey();
    }, [refreshSurvey]);

    useEffect(() => {
        if (searchParams.get("error") === "true") {
            setStepperError(true);
        }
    }, [searchParams]);

    const testLoaded = test && typeof test === "object";

    const isAIGenerated = useMemo(
        () => surveyQuestions.some((q) => Number(q?.is_ai_generated) >= 1),
        [surveyQuestions],
    );

    const handleShowModal = useCallback((type) => {
        setAiModalType(type);
        setShowAiSurveyModal(true);
    }, []);

    const enterAiSurveyCreatorMode = useCallback((mode) => {
        if (mode === "AI") {
            setSurveyViewMode("ai");
        }
    }, []);

    const showSaveError = useCallback((message) => {
        toast(
            <ToastMessage
                type="error"
                message={message}
                closable={false}
                onClose={() => toast.dismiss()}
            />,
            {
                className: "errortoast",
                position: "bottom-center",
                duration: settings.toast_duration,
            },
        );
    }, []);

    const showJumpLogicUpdatedToast = useCallback((message) => {
        toast(
            <ToastMessage
                type="logic"
                message={message}
                closable={false}
                onClose={() => toast.dismiss()}
            />,
            {
                position: "bottom-center",
                duration: settings.toast_duration,
            },
        );
    }, []);

    const persistSurveyQuestions = useCallback(
        async (questionsPayload) => {
            const token = ReactSession.get("token");
            const body = surveyQuestionsToPutPayload(questionsPayload);
            setIsSaved({ status: "loading", message: "" });
            try {
                const response = await putTestSurveyQuestions(test_id, body, token);
                if (response?.success) {
                    setIsSaved({ status: "success", message: "" });
                    getTestLength(test_id);
                    await refreshSurvey({ silent: true });
                } else {
                    setIsSaved({ status: "error", message: response?.message || "" });
                    showSaveError(response?.message || "Could not save survey questions.");
                    await refreshSurvey({ silent: true });
                }
            } catch (err) {
                setIsSaved({ status: "error", message: String(err) });
                showSaveError(typeof err === "string" ? err : "Could not save survey questions.");
                await refreshSurvey({ silent: true });
            }
        },
        [test_id, refreshSurvey, getTestLength, showSaveError],
    );

    /** Clone one question after itself and PUT (new row — strip DB id). */
    const duplicateSurveyQuestion = useCallback(
        (index) => {
            if (editingSurveyIndex !== null) {
                showSaveError("Finish editing the current question before duplicating.");
                return;
            }
            if (!Number.isInteger(index) || index < 0 || index >= surveyQuestions.length) {
                return;
            }
            if (surveyQuestions.length >= 5000) {
                showSaveError(
                    "You've hit the maximum amount of test questions! If you need to add more, consider removing earlier ones.",
                );
                return;
            }
            const copy = cloneDeep(surveyQuestions[index]);
            delete copy._surveyQuestionPk;
            delete copy.__surveyClientKey;
            const copyReady = stripQuestionTransientIds(copy);
            const next = normalizeSurveyQuestionSequentialIds([
                ...surveyQuestions.slice(0, index + 1),
                copyReady,
                ...surveyQuestions.slice(index + 1),
            ]);
            setSurveyQuestions(next);
            setStepperError(false);
            persistSurveyQuestions(next);
        },
        [surveyQuestions, editingSurveyIndex, persistSurveyQuestions, showSaveError],
    );

    /** Remove one question and PUT the remaining list (same survey endpoint). */
    const confirmDeleteSurveyQuestion = useCallback(() => {
        const index = pendingDeleteSurveyIndexRef.current;
        const hadJumpRules = pendingDeleteHadJumpRulesRef.current;
        const affectedKeys = pendingDeleteAffectedParentsRef.current;
        pendingDeleteSurveyIndexRef.current = null;
        pendingDeleteHadJumpRulesRef.current = false;
        pendingDeleteAffectedParentsRef.current = [];
        setDeleteQuestionConfirmModal({
            open: false,
            confirm_title: "",
            confirm_message: "",
            cancel_btn_title: "",
            confirm_btn_title: "",
        });

        if (!Number.isInteger(index) || index < 0 || index >= surveyQuestions.length) {
            return;
        }
        const next = normalizeSurveyQuestionSequentialIds(
            applySurveyQuestionDeleteWithJumpResets(surveyQuestions, index),
        );
        setSurveyQuestions(next);
        setStepperError(false);
        persistSurveyQuestions(next);
        if (hadJumpRules && affectedKeys.length > 0) {
            setJumpWarningKeys((prev) => new Set([...prev, ...affectedKeys]));
        }
    }, [surveyQuestions, persistSurveyQuestions]);

    const cancelDeleteSurveyQuestion = useCallback(() => {
        pendingDeleteSurveyIndexRef.current = null;
        pendingDeleteHadJumpRulesRef.current = false;
        pendingDeleteAffectedParentsRef.current = [];
        setDeleteQuestionConfirmModal({
            open: false,
            confirm_title: "",
            confirm_message: "",
            cancel_btn_title: "",
            confirm_btn_title: "",
        });
    }, []);

    const handleSurveyQuestionDragEnd = useCallback(
        (result) => {
            if (!result.destination) {
                return;
            }
            if (editingSurveyIndex !== null) {
                return;
            }
            const src = result.source.index;
            const dest = result.destination.index;
            if (src === dest) {
                return;
            }

            const prevOrder = surveyQuestions;
            const reordered = Array.from(surveyQuestions);
            const [removed] = reordered.splice(src, 1);
            reordered.splice(dest, 0, removed);

            let next = remapSurveyJumpTargetsAfterReorder(prevOrder, reordered);
            next = normalizeSurveyQuestionSequentialIds(next);

            const violations = findBackwardJumpViolations(next);
            if (violations.length === 0) {
                setSurveyQuestions(next);
                setStepperError(false);
                persistSurveyQuestions(next);
                return;
            }

            pendingSurveyReorderRef.current = { next, violations };
            const isAr = test && typeof test === "object" && test.language === "ar";
            const rulesText = buildViolationRulesText(violations, next);
            setReorderLogicConfirmModal({
                open: true,
                confirm_title: isAr ? "تأكيد إعادة الترتيب" : "Confirm reorder",
                confirm_message: isAr
                    ? `سيؤثر هذا الإجراء على قواعد المنطق التالية: ${rulesText}. سيتم مسح هذه القواعد ويجب ضبطها من جديد. هل تريد المتابعة؟`
                    : `This action will affect the following logic rules: ${rulesText}. These rules will be cleared and need to be set again. Continue?`,
                cancel_btn_title: isAr ? "إلغاء" : "Cancel",
                confirm_btn_title: isAr ? "تأكيد" : "Confirm",
            });
        },
        [surveyQuestions, editingSurveyIndex, persistSurveyQuestions, test],
    );

    const confirmReorderWithViolations = useCallback(() => {
        const pending = pendingSurveyReorderRef.current;
        pendingSurveyReorderRef.current = null;
        setReorderLogicConfirmModal({
            open: false,
            confirm_title: "",
            confirm_message: "",
            cancel_btn_title: "",
            confirm_btn_title: "",
        });
        if (!pending) {
            return;
        }
        const { next: proposed, violations } = pending;
        const affectedKeys = violations
            .map((v) => getQuestionWarningKey(proposed[v.parentIndex]))
            .filter(Boolean);
        const fixed = normalizeSurveyQuestionSequentialIds(fixBackwardJumpsInSurvey(proposed));
        setSurveyQuestions(fixed);
        setStepperError(false);
        persistSurveyQuestions(fixed);
        if (affectedKeys.length > 0) {
            setJumpWarningKeys((prev) => new Set([...prev, ...affectedKeys]));
        }
    }, [persistSurveyQuestions]);

    const cancelReorderWithViolations = useCallback(() => {
        pendingSurveyReorderRef.current = null;
        setReorderLogicConfirmModal({
            open: false,
            confirm_title: "",
            confirm_message: "",
            cancel_btn_title: "",
            confirm_btn_title: "",
        });
    }, []);

    const deleteSurveyQuestion = useCallback(
        (index) => {
            if (!Number.isInteger(index) || index < 0 || index >= surveyQuestions.length) {
                return;
            }
            const rules = collectSurveyJumpRulesTargetingIndex(index, surveyQuestions);
            if (rules.length > 0) {
                const keys = [
                    ...new Set(rules.map((r) => `Q${r.fromDisplay} → Q${r.toDisplay}`)),
                ];
                const rulesText = keys.join(", ");
                const isAr =
                    test && typeof test === "object" && test.language === "ar";
                pendingDeleteSurveyIndexRef.current = index;
                pendingDeleteHadJumpRulesRef.current = true;
                pendingDeleteAffectedParentsRef.current = rules
                    .map((r) => getQuestionWarningKey(surveyQuestions[r.parentIndex]))
                    .filter(Boolean);
                setDeleteQuestionConfirmModal({
                    open: true,
                    confirm_title: isAr ? "تأكيد الحذف" : "Confirm deletion",
                    confirm_message: isAr
                        ? `سيؤثر هذا الإجراء على قواعد المنطق التالية: ${rulesText}. سيتم مسح هذه القواعد ويجب ضبطها من جديد. هل تريد المتابعة؟`
                        : `This action will affect the following logic rules: ${rulesText}. These rules will be cleared and need to be set again. Continue?`,
                    cancel_btn_title: isAr ? "إلغاء" : "Cancel",
                    confirm_btn_title: isAr ? "تأكيد" : "Confirm",
                });
                return;
            }
            const next = normalizeSurveyQuestionSequentialIds(
                surveyQuestions.filter((_, i) => i !== index),
            );
            setSurveyQuestions(next);
            setStepperError(false);
            persistSurveyQuestions(next);
        },
        [surveyQuestions, persistSurveyQuestions, test],
    );

    /** Enable/disable branching logic and persist (v2 survey PUT). */
    const toggleSurveyQuestionLogic = useCallback(
        (logicStatus, questionIndex) => {
            if (editingSurveyIndex !== null) {
                showSaveError("Finish editing the question before changing logic.");
                return;
            }
            if (
                !Number.isInteger(questionIndex) ||
                questionIndex < 0 ||
                questionIndex >= surveyQuestions.length
            ) {
                return;
            }
            const on = logicStatus === 1 || logicStatus === true;
            const q = surveyQuestions[questionIndex];
            const next = [...surveyQuestions];
            const updated = { ...q, is_logic: on ? 1 : 0 };

            if (on) {
                if (q.questionType === "singlechoice") {
                    const oc = extractOptionLabelsFromQuestion(q).length;
                    const jumpTo = [];
                    const defaultJump = getDefaultForwardJumpTarget(
                        questionIndex + 1,
                        surveyQuestions,
                    );
                    for (let oi = 0; oi < oc; oi++) {
                        jumpTo.push(defaultJump);
                    }
                    updated.jumpTo = jumpTo;
                } else {
                    updated.jump_to_question = getDefaultForwardJumpTarget(
                        questionIndex + 1,
                        surveyQuestions,
                    );
                }
            } else {
                if (q.questionType === "singlechoice") {
                    updated.jumpTo = [];
                } else {
                    updated.jump_to_question = "";
                }
            }

            next[questionIndex] = updated;
            const normalized = normalizeSurveyQuestionSequentialIds(next);
            setSurveyQuestions(normalized);
            persistSurveyQuestions(normalized);
        },
        [surveyQuestions, editingSurveyIndex, persistSurveyQuestions, showSaveError],
    );

    /**
     * Update jump targets from Question: singlechoice uses (optionIndex, value);
     * other types use (questionIndex, value) but Question passes question index as first arg — we use map closure.
     * If the new jump creates a backward reference or a cycle, a confirmation modal is shown instead
     * of persisting. Confirming resets that jump to the next valid question; cancelling reverts.
     */
    const saveSurveyQuestionJump = useCallback(
        (questionIndex, optionIndex, value, isSingleChoice) => {
            if (editingSurveyIndex !== null) {
                showSaveError("Finish editing the question before changing jump logic.");
                return;
            }
            if (
                !Number.isInteger(questionIndex) ||
                questionIndex < 0 ||
                questionIndex >= surveyQuestions.length
            ) {
                return;
            }
            const q = surveyQuestions[questionIndex];
            const next = [...surveyQuestions];

            if (isSingleChoice) {
                const oc = Math.max(
                    extractOptionLabelsFromQuestion(q).length,
                    Array.isArray(q.jumpTo) ? q.jumpTo.length : 0,
                );
                const jumpTo = Array.isArray(q.jumpTo) ? [...q.jumpTo] : [];
                while (jumpTo.length < oc) {
                    jumpTo.push(
                        getDefaultForwardJumpTarget(questionIndex + 1, surveyQuestions),
                    );
                }
                if (optionIndex >= 0 && optionIndex < jumpTo.length) {
                    jumpTo[optionIndex] = value;
                }
                next[questionIndex] = { ...q, jumpTo };
            } else {
                next[questionIndex] = { ...q, jump_to_question: value };
            }

            const normalized = normalizeSurveyQuestionSequentialIds(next);

            const isBackward = isBackwardOrSameJump(questionIndex, value, normalized);
            const hasCycle = surveyJumpGraphHasCycleFromStart(normalized);

            if (isBackward || hasCycle) {
                const isAr = test && typeof test === "object" && test.language === "ar";
                pendingJumpUpdateRef.current = {
                    next: normalized,
                    questionIndex,
                    optionIndex,
                    isSingleChoice,
                };
                setJumpLogicViolationModal({
                    open: true,
                    confirm_title: isAr ? "منطق انتقال غير صالح" : "Invalid jump logic",
                    confirm_message: isAr
                        ? (hasCycle
                            ? "يُنشئ هذا الانتقال حلقة دورية في الاستطلاع. هل تريد إعادة تعيينه إلى أقرب سؤال صالح أو نهاية الاستطلاع؟"
                            : "يشير هذا الانتقال إلى سؤال سابق أو نفس السؤال. يجب أن تنتقل القفزات إلى الأمام فقط. هل تريد إعادة تعيينه إلى أقرب سؤال صالح أو نهاية الاستطلاع؟")
                        : (hasCycle
                            ? "This jump creates a circular loop in the survey. Would you like to reset it to the next valid question or End Survey?"
                            : "This jump points to a question that appears before or at the same position. Jumps must go forward only. Would you like to reset it to the next valid question or End Survey?"),
                    cancel_btn_title: isAr ? "إلغاء" : "Cancel",
                    confirm_btn_title: isAr ? "إعادة تعيين" : "Reset",
                });
                return;
            }

            const warnKey = getQuestionWarningKey(surveyQuestions[questionIndex]);
            if (warnKey) {
                setJumpWarningKeys((prev) => {
                    const next = new Set(prev);
                    next.delete(warnKey);
                    return next;
                });
            }
            setSurveyQuestions(normalized);
            persistSurveyQuestions(normalized);
        },
        [surveyQuestions, editingSurveyIndex, persistSurveyQuestions, showSaveError, test],
    );

    const confirmJumpLogicFix = useCallback(() => {
        const pending = pendingJumpUpdateRef.current;
        pendingJumpUpdateRef.current = null;
        setJumpLogicViolationModal({
            open: false,
            confirm_title: "",
            confirm_message: "",
            cancel_btn_title: "",
            confirm_btn_title: "",
        });
        if (!pending) {
            return;
        }
        const { next, questionIndex, optionIndex, isSingleChoice } = pending;
        const q = next[questionIndex];
        const def = getDefaultForwardJumpTarget(questionIndex + 1, next);
        const fixed = [...next];

        if (isSingleChoice) {
            const jumpTo = Array.isArray(q.jumpTo) ? [...q.jumpTo] : [];
            if (optionIndex !== null && optionIndex >= 0 && optionIndex < jumpTo.length) {
                jumpTo[optionIndex] = def;
            }
            fixed[questionIndex] = { ...q, jumpTo };
        } else {
            fixed[questionIndex] = { ...q, jump_to_question: def, jumpTo: def };
        }

        const normalized = normalizeSurveyQuestionSequentialIds(fixed);
        const warnKey = getQuestionWarningKey(q);
        if (warnKey) {
            setJumpWarningKeys((prev) => {
                const s = new Set(prev);
                s.delete(warnKey);
                return s;
            });
        }
        setSurveyQuestions(normalized);
        persistSurveyQuestions(normalized);
    }, [persistSurveyQuestions]);

    const cancelJumpLogicFix = useCallback(() => {
        pendingJumpUpdateRef.current = null;
        setJumpLogicViolationModal({
            open: false,
            confirm_title: "",
            confirm_message: "",
            cancel_btn_title: "",
            confirm_btn_title: "",
        });
    }, []);

    const cancelQuestionHandler = useCallback(() => {
        setQuestionType("");
        setEditingSurveyIndex(null);
    }, []);

    const surveyValidateForm = useCallback(() => {
        const checkIntroductionQuestionIfErrorExits =
            introductionQuestionRef.current?.questionValidateForm(true) ?? true;

        if (
            !checkIntroductionQuestionIfErrorExits &&
            checkIntroductionQuestionIfErrorExits !== undefined
        ) {
            return false;
        }

        if (editingSurveyIndex !== null && questionInputValidateRef.current) {
            const valid = questionInputValidateRef.current.handleQuestionSubmit(true);
            if (!valid) {
                return false;
            }
        }

        const checkConclusionQuestionIfErrorExits =
            conclusionQuestionRef.current?.questionValidateForm(true) ?? true;

        if (
            !checkConclusionQuestionIfErrorExits &&
            checkConclusionQuestionIfErrorExits !== undefined
        ) {
            return false;
        }

        if (surveyQuestions.length === 0) {
            setStepperError(true);
            setSurveyError("At least one question is required");
            showSaveError(
                "Please add at least one question for the survey before proceeding.",
            );
            const surveyErrorElement = document.getElementById("survey-error");
            if (surveyErrorElement) {
                surveyErrorElement.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            return false;
        }

        setStepperError(false);
        setSurveyError(null);
        navigate("/r/test/" + test_id + "/thank-you/");
        return true;
    }, [editingSurveyIndex, navigate, questionInputValidateRef, showSaveError, surveyQuestions.length, test_id]);

    useEffect(() => {
        if (surveyQuestions.length > 0 && surveyError) {
            setSurveyError(null);
        }
    }, [surveyQuestions.length, surveyError]);

    return (
        <LayoutResearcher
            wrapClass={`create-test-data-wrapper${testLoaded && test.language === "ar" ? " arabic_wrapper_main" : ""
                }`}
            openSupportPanel={show_support_panel}
            hideSupportPanel={() => {
                setShowSupportPanel(false);
            }}
        >
            <Helmet>
                <title>{getPageTitle("Create " + (testLoaded && test.methodology ? renderTestTitleCreate(test.methodology) : "test") + " - step 2")}</title>
            </Helmet>
            <div className="three-column-layout">
                <Header test={test} isLoading={isLoading} isSaved={isSaved} />
                <div className="three-column-layout-body">
                    <div className="three-column-layout-left">
                        <Stepper
                            test={test}
                            completed_step={1}
                            stepCallback={() => {
                                /* Reserved: persist survey before stepper navigation when save flow exists */
                            }}
                            error={stepperError}
                        />
                    </div>
                    {isLoading && (
                        <div className="test-section-loader">
                            <LoadingIcon />
                        </div>
                    )}
                    {!isLoading && (
                        <>
                            {surveyGoalModal?.open && (
                                <HelpModal
                                    cssClass="survey-goal-modal"
                                    open={surveyGoalModal.open}
                                    close={() => {
                                        setSurveyGoalModal({ ...surveyGoalModal, open: false });
                                    }}
                                    heading="Survey goal"
                                    body={
                                        <div className="prototype-criteria-modal-data">
                                            <div className="mb-32">
                                                <Text
                                                    cssClasses="gray-text"
                                                    type="body-text-3"
                                                    fontWeight="medium-font"
                                                >
                                                    {test.survey_goal ||
                                                        ReactSession.get("last_ai_survey_goal") ||
                                                        "—"}
                                                </Text>
                                            </div>

                                            <div className="d-flex justify-content-between duplicate-test-modal-action">
                                                <Button
                                                    label="Edit and regenerate"
                                                    type="primary"
                                                    size="large"
                                                    iconLeft={
                                                        <Icon value="edit" colorClass="gray-50-svg" />
                                                    }
                                                    onClick={() => {
                                                        ReactSession.set(
                                                            "survey_goal",
                                                            test.survey_goal ||
                                                            ReactSession.get("last_ai_survey_goal") ||
                                                            "",
                                                        );
                                                        enterAiSurveyCreatorMode("AI");
                                                        setSurveyGoalModal({
                                                            ...surveyGoalModal,
                                                            open: false,
                                                        });
                                                    }}
                                                />
                                                <Button
                                                    label="Copy survey goal"
                                                    type="primary"
                                                    size="large"
                                                    iconLeft={
                                                        <Icon value="duplicate" colorClass="gray-50-svg" />
                                                    }
                                                    onClick={() => {
                                                        copyToClipboard(
                                                            test.survey_goal ||
                                                            ReactSession.get("last_ai_survey_goal") ||
                                                            "",
                                                        );
                                                        showToast("Survey goal copied", "success");
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    }
                                />
                            )}
                            <AIModal
                                close={() => setShowAiSurveyModal(false)}
                                showModal={showAiSurveyModal}
                                setSurveyCreatorMode={(mode) => {
                                    enterAiSurveyCreatorMode(mode);
                                    setShowAiSurveyModal(false);
                                }}
                                type={aiModalType}
                            />
                            <div className="three-column-layout-center">
                                {surveyViewMode === "ai" ? (
                                    <SurveyCreatorAI
                                        switchToManual={switchSurveyAiToManual}
                                    />
                                ) : (
                                    <div className="create-prefer-text-wrapper">

                                        <div className="card-sorting-creating-test-wrap">
                                            <div className="section-minimum-height">
                                                {loadError && (
                                                    <Text type="body-text-2" cssClasses="color-red">
                                                        {loadError}
                                                    </Text>
                                                )}

                                                {!loadError && testLoaded && (
                                                    <>
                                                        <Accordian
                                                            title="Introduction questions"
                                                            subtitle="(Optional)"
                                                            tooltip="Some cool text"
                                                            open={Boolean(accordians.introduction)}
                                                            onChange={() => {
                                                                const next = { ...accordians };
                                                                next.introduction = !accordians.introduction;
                                                                setAccordians(next);
                                                            }}
                                                            questionCount={introCount}
                                                        >
                                                            <IntroductionQuestions
                                                                ref={introductionQuestionRef}
                                                                questions={introductionQuestions}
                                                                test={test}
                                                                setLoading={(status) => setIsSaved(status)}
                                                                changeCount={(n) => setIntroCount(n)}
                                                                updateQuestions={(q) =>
                                                                    setIntroductionQuestions([...q])
                                                                }
                                                            />
                                                        </Accordian>

                                                        <Accordian
                                                            cssClasses="generate-with-AI-accordian"
                                                            title={<>Survey</>}
                                                            subtitle={
                                                                <>
                                                                    {isAIGenerated === true && (
                                                                        <>
                                                                            <Icon
                                                                                value="eye"
                                                                                size="medium"
                                                                                cssClasses="none-for-big-screen ai-icon mr-2"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    setSurveyGoalModal({
                                                                                        ...surveyGoalModal,
                                                                                        open: true,
                                                                                    });
                                                                                }}
                                                                            />
                                                                            <Button
                                                                                type="ghost"
                                                                                size="medium"
                                                                                cssclass="none-for-small-screen "
                                                                                iconLeft={
                                                                                    <Icon value="eye" size="medium" />
                                                                                }
                                                                                label="View survey goal"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    setSurveyGoalModal({
                                                                                        ...surveyGoalModal,
                                                                                        open: true,
                                                                                    });
                                                                                }}
                                                                            />
                                                                        </>
                                                                    )}

                                                                    <Button
                                                                        type="primary"
                                                                        size="medium"
                                                                        cssclass="navy-btn ai-btn"
                                                                        iconLeft={
                                                                            <Icon
                                                                                value="Ai"
                                                                                size="medium"
                                                                                colorClass="gray-50-svg"
                                                                                cssClasses="tag-reverse "
                                                                            />
                                                                        }
                                                                        label={
                                                                            isAIGenerated === true
                                                                                ? "Regenerate [BETA]"
                                                                                : "Generate with AI [BETA]"
                                                                        }
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            if (isAIGenerated) {
                                                                                handleShowModal("regenerate");
                                                                            } else if (surveyQuestions.length > 0) {
                                                                                handleShowModal("generate");
                                                                            } else {
                                                                                enterAiSurveyCreatorMode("AI");
                                                                            }
                                                                        }}
                                                                    />
                                                                </>
                                                            }
                                                            tooltip="Some cool text"
                                                            open={accordians.survey ? true : false}
                                                            onChange={(e) => {
                                                                if (e) {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                }
                                                                const new_accordians = { ...accordians };
                                                                if (accordians.survey) {
                                                                    new_accordians.survey = false;
                                                                } else {
                                                                    new_accordians.survey = true;
                                                                }
                                                                setAccordians(new_accordians);
                                                            }}
                                                            questionCount={surveyQuestions.length}
                                                        >
                                                            <>
                                                                {testLoaded && (
                                                                    <>
                                                                        <ConfirmationModal
                                                                            openModal={
                                                                                reorderLogicConfirmModal.open
                                                                            }
                                                                            confirm_title={
                                                                                reorderLogicConfirmModal.confirm_title
                                                                            }
                                                                            confirm_message={
                                                                                reorderLogicConfirmModal.confirm_message
                                                                            }
                                                                            cancel_btn_title={
                                                                                reorderLogicConfirmModal.cancel_btn_title
                                                                            }
                                                                            confirm_btn_title={
                                                                                reorderLogicConfirmModal.confirm_btn_title
                                                                            }
                                                                            close={cancelReorderWithViolations}
                                                                            confirm={confirmReorderWithViolations}
                                                                            cssClass={
                                                                                test.language === "ar"
                                                                                    ? "arabic_wrapper"
                                                                                    : ""
                                                                            }
                                                                        />
                                                                        <ConfirmationModal
                                                                            openModal={
                                                                                jumpLogicViolationModal.open
                                                                            }
                                                                            confirm_title={
                                                                                jumpLogicViolationModal.confirm_title
                                                                            }
                                                                            confirm_message={
                                                                                jumpLogicViolationModal.confirm_message
                                                                            }
                                                                            cancel_btn_title={
                                                                                jumpLogicViolationModal.cancel_btn_title
                                                                            }
                                                                            confirm_btn_title={
                                                                                jumpLogicViolationModal.confirm_btn_title
                                                                            }
                                                                            close={cancelJumpLogicFix}
                                                                            confirm={confirmJumpLogicFix}
                                                                            cssClass={
                                                                                test.language === "ar"
                                                                                    ? "arabic_wrapper"
                                                                                    : ""
                                                                            }
                                                                        />
                                                                        <ConfirmationModal
                                                                            openModal={
                                                                                deleteQuestionConfirmModal.open
                                                                            }
                                                                            confirm_title={
                                                                                deleteQuestionConfirmModal.confirm_title
                                                                            }
                                                                            confirm_message={
                                                                                deleteQuestionConfirmModal.confirm_message
                                                                            }
                                                                            cancel_btn_title={
                                                                                deleteQuestionConfirmModal.cancel_btn_title
                                                                            }
                                                                            confirm_btn_title={
                                                                                deleteQuestionConfirmModal.confirm_btn_title
                                                                            }
                                                                            close={cancelDeleteSurveyQuestion}
                                                                            confirm={confirmDeleteSurveyQuestion}
                                                                            cssClass={
                                                                                test.language === "ar"
                                                                                    ? "arabic_wrapper"
                                                                                    : ""
                                                                            }
                                                                        />
                                                                    </>
                                                                )}
                                                                <>
                                                                    {surveyQuestions.length > 0 && (
                                                                        <DragDropContext
                                                                            onDragEnd={handleSurveyQuestionDragEnd}
                                                                        >
                                                                            <Droppable droppableId="survey-questions-manual-list">
                                                                                {(droppableProvided) => (
                                                                                    <div
                                                                                        className="survey-questions-preview-list"
                                                                                        ref={
                                                                                            droppableProvided.innerRef
                                                                                        }
                                                                                        {...droppableProvided.droppableProps}
                                                                                    >
                                                                                        {surveyQuestions.map(
                                                                                            (
                                                                                                question,
                                                                                                index,
                                                                                            ) => (
                                                                                                <Draggable
                                                                                                    key={getSurveyQuestionStableKey(
                                                                                                        question,
                                                                                                        index,
                                                                                                    )}
                                                                                                    draggableId={getSurveyQuestionStableKey(
                                                                                                        question,
                                                                                                        index,
                                                                                                    )}
                                                                                                    index={index}
                                                                                                    isDragDisabled={
                                                                                                        editingSurveyIndex !==
                                                                                                        null ||
                                                                                                        isSaved.status ===
                                                                                                        "loading"
                                                                                                    }
                                                                                                >
                                                                                                    {(dragProvided) => (
                                                                                                        <div
                                                                                                            ref={
                                                                                                                dragProvided.innerRef
                                                                                                            }
                                                                                                            {...dragProvided.draggableProps}
                                                                                                            {...dragProvided.dragHandleProps}
                                                                                                            className={`survey-question-preview mb-16${editingSurveyIndex === index ? "" : " grey-bg"}`}
                                                                                                        >
                                                                                                            {editingSurveyIndex ===
                                                                                                                index ? (
                                                                                                                <div
                                                                                                                    className={`question-answer-holder ${test.language ===
                                                                                                                        "ar"
                                                                                                                        ? "arabic_wrapper"
                                                                                                                        : ""
                                                                                                                        }`}
                                                                                                                >
                                                                                                                    <div
                                                                                                                        className={`lightgraybox questionbox-s newsingle-question added-question-card-wrap ${question.questionType ===
                                                                                                                            "singlechoice"
                                                                                                                            ? "singlesurvey-view"
                                                                                                                            : ""
                                                                                                                            }`}
                                                                                                                    >
                                                                                                                        <QuestionInput
                                                                                                                            key={`edit-${editingSurveyIndex}`}
                                                                                                                            questionType={
                                                                                                                                questionType
                                                                                                                            }
                                                                                                                            cancelQuestion={
                                                                                                                                cancelQuestionHandler
                                                                                                                            }
                                                                                                                            ref={
                                                                                                                                questionInputValidateRef
                                                                                                                            }
                                                                                                                            editingQuestion={
                                                                                                                                surveyQuestions[
                                                                                                                                editingSurveyIndex
                                                                                                                                ]
                                                                                                                            }
                                                                                                                            methodology="Survey"
                                                                                                                            islogic={Boolean(
                                                                                                                                questionType,
                                                                                                                            )}
                                                                                                                            addQuestions={(
                                                                                                                                qPayload,
                                                                                                                            ) => {
                                                                                                                                const prev =
                                                                                                                                    surveyQuestions[
                                                                                                                                    editingSurveyIndex
                                                                                                                                    ];
                                                                                                                                const merged =
                                                                                                                                {
                                                                                                                                    ...stripQuestionTransientIds(
                                                                                                                                        qPayload,
                                                                                                                                    ),
                                                                                                                                    _surveyQuestionPk:
                                                                                                                                        prev?._surveyQuestionPk,
                                                                                                                                    is_ai_generated:
                                                                                                                                        prev?.is_ai_generated ??
                                                                                                                                        qPayload.is_ai_generated ??
                                                                                                                                        0,
                                                                                                                                };
                                                                                                                                const next =
                                                                                                                                    normalizeSurveyQuestionSequentialIds(
                                                                                                                                        surveyQuestions.map(
                                                                                                                                            (
                                                                                                                                                qq,
                                                                                                                                                i,
                                                                                                                                            ) =>
                                                                                                                                                i ===
                                                                                                                                                    editingSurveyIndex
                                                                                                                                                    ? merged
                                                                                                                                                    : qq,
                                                                                                                                        ),
                                                                                                                                    );
                                                                                                                                setSurveyQuestions(
                                                                                                                                    next,
                                                                                                                                );
                                                                                                                                cancelQuestionHandler();
                                                                                                                                setStepperError(
                                                                                                                                    false,
                                                                                                                                );
                                                                                                                                persistSurveyQuestions(
                                                                                                                                    next,
                                                                                                                                );
                                                                                                                            }}
                                                                                                                            language={
                                                                                                                                test.language
                                                                                                                            }
                                                                                                                            questionno={
                                                                                                                                editingSurveyIndex +
                                                                                                                                1
                                                                                                                            }
                                                                                                                            questionlist={
                                                                                                                                surveyQuestions
                                                                                                                            }
                                                                                                                            changeQuestionType={(qt) =>
                                                                                                                                setQuestionType(qt)
                                                                                                                            }
                                                                                                                        />
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            ) : (
                                                                                                                <div
                                                                                                                    className={`question-answer-holder ${test.language ===
                                                                                                                        "ar"
                                                                                                                        ? "arabic_wrapper"
                                                                                                                        : ""
                                                                                                                        }`}
                                                                                                                >
                                                                                                                    <Question
                                                                                                                        readOnly={false}
                                                                                                                        hideDuplicateAction={
                                                                                                                            false
                                                                                                                        }
                                                                                                                        duplicateProgress={
                                                                                                                            isSaved.status ===
                                                                                                                            "loading"
                                                                                                                        }
                                                                                                                        index={index}
                                                                                                                        question={
                                                                                                                            question
                                                                                                                        }
                                                                                                                        onCopy={
                                                                                                                            duplicateSurveyQuestion
                                                                                                                        }
                                                                                                                        onEdit={(idx) => {
                                                                                                                            if (
                                                                                                                                !Number.isInteger(
                                                                                                                                    idx,
                                                                                                                                ) ||
                                                                                                                                idx < 0 ||
                                                                                                                                idx >=
                                                                                                                                surveyQuestions.length
                                                                                                                            ) {
                                                                                                                                return;
                                                                                                                            }
                                                                                                                            setEditingSurveyIndex(
                                                                                                                                idx,
                                                                                                                            );
                                                                                                                            setQuestionType(
                                                                                                                                surveyQuestions[
                                                                                                                                    idx
                                                                                                                                ]
                                                                                                                                    .questionType,
                                                                                                                            );
                                                                                                                            setStepperError(
                                                                                                                                false,
                                                                                                                            );
                                                                                                                        }}
                                                                                                                        onDelete={
                                                                                                                            deleteSurveyQuestion
                                                                                                                        }
                                                                                                                        allQuestions={
                                                                                                                            surveyQuestions
                                                                                                                        }
                                                                                                                        saveJumpTo={(
                                                                                                                            first,
                                                                                                                            value,
                                                                                                                        ) => {
                                                                                                                            const qType =
                                                                                                                                surveyQuestions[
                                                                                                                                    index
                                                                                                                                ]
                                                                                                                                    ?.questionType;
                                                                                                                            if (
                                                                                                                                qType ===
                                                                                                                                "singlechoice"
                                                                                                                            ) {
                                                                                                                                saveSurveyQuestionJump(
                                                                                                                                    index,
                                                                                                                                    first,
                                                                                                                                    value,
                                                                                                                                    true,
                                                                                                                                );
                                                                                                                            } else {
                                                                                                                                saveSurveyQuestionJump(
                                                                                                                                    index,
                                                                                                                                    null,
                                                                                                                                    value,
                                                                                                                                    false,
                                                                                                                                );
                                                                                                                            }
                                                                                                                        }}
                                                                                                                        type="survey"
                                                                                                                        onToggleLogic={
                                                                                                                            toggleSurveyQuestionLogic
                                                                                                                        }
                                                                                                                        language={
                                                                                                                            test.language ||
                                                                                                                            "en"
                                                                                                                        }
                                                                                                                        hasJumpWarning={jumpWarningKeys.has(
                                                                                                                            getQuestionWarningKey(question),
                                                                                                                        )}
                                                                                                                    />
                                                                                                                </div>
                                                                                                            )}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </Draggable>
                                                                                            ),
                                                                                        )}
                                                                                        {
                                                                                            droppableProvided.placeholder
                                                                                        }
                                                                                    </div>
                                                                                )}
                                                                            </Droppable>
                                                                        </DragDropContext>
                                                                    )}

                                                                    {surveyQuestions.length === 0 &&
                                                                        editingSurveyIndex === null && (
                                                                            <Text type="body-text-2" cssClasses="mb-16">
                                                                                No survey questions on this test yet.
                                                                            </Text>
                                                                        )}

                                                                    {questionType === "" &&
                                                                        editingSurveyIndex === null && (
                                                                            <div
                                                                                className={`ans-list-wrap question-prev-wrap survey-test-option-wrap mb-0 mt-20 ${test.language === "ar" ? "arabic_wrapper" : ""}`}
                                                                            >
                                                                                <div className="add-more-question-option">
                                                                                    <div className="stud-tiles-hold">
                                                                                        <QuestionCard
                                                                                            questiontype="Free Text"
                                                                                            onClick={() => {
                                                                                                if (
                                                                                                    surveyQuestions.length >=
                                                                                                    5000
                                                                                                ) {
                                                                                                    showSaveError(
                                                                                                        "You've hit the maximum amount of test questions! If you need to add more, consider removing earlier ones.",
                                                                                                    );
                                                                                                    return;
                                                                                                }
                                                                                                setQuestionType("free");
                                                                                            }}
                                                                                        />
                                                                                        <QuestionCard
                                                                                            questiontype="Single choice"
                                                                                            onClick={() => {
                                                                                                if (
                                                                                                    surveyQuestions.length >=
                                                                                                    5000
                                                                                                ) {
                                                                                                    showSaveError(
                                                                                                        "You've hit the maximum amount of test questions! If you need to add more, consider removing earlier ones.",
                                                                                                    );
                                                                                                    return;
                                                                                                }
                                                                                                setQuestionType(
                                                                                                    "singlechoice",
                                                                                                );
                                                                                            }}
                                                                                        />
                                                                                        <QuestionCard
                                                                                            questiontype="Multiple-choice"
                                                                                            onClick={() => {
                                                                                                if (
                                                                                                    surveyQuestions.length >=
                                                                                                    5000
                                                                                                ) {
                                                                                                    showSaveError(
                                                                                                        "You've hit the maximum amount of test questions! If you need to add more, consider removing earlier ones.",
                                                                                                    );
                                                                                                    return;
                                                                                                }
                                                                                                setQuestionType(
                                                                                                    "multiplechoice",
                                                                                                );
                                                                                            }}
                                                                                        />
                                                                                        <QuestionCard
                                                                                            questiontype="Likert scale"
                                                                                            onClick={() => {
                                                                                                if (
                                                                                                    surveyQuestions.length >=
                                                                                                    5000
                                                                                                ) {
                                                                                                    showSaveError(
                                                                                                        "You've hit the maximum amount of test questions! If you need to add more, consider removing earlier ones.",
                                                                                                    );
                                                                                                    return;
                                                                                                }
                                                                                                setQuestionType(
                                                                                                    "likertscale_single",
                                                                                                );
                                                                                            }}
                                                                                        />
                                                                                        <QuestionCard
                                                                                            questiontype="Likert matrix"
                                                                                            onClick={() => {
                                                                                                if (
                                                                                                    surveyQuestions.length >=
                                                                                                    5000
                                                                                                ) {
                                                                                                    showSaveError(
                                                                                                        "You've hit the maximum amount of test questions! If you need to add more, consider removing earlier ones.",
                                                                                                    );
                                                                                                    return;
                                                                                                }
                                                                                                setQuestionType(
                                                                                                    "likertscale",
                                                                                                );
                                                                                            }}
                                                                                        />
                                                                                        <QuestionCard
                                                                                            questiontype="Rating scale"
                                                                                            onClick={() => {
                                                                                                if (
                                                                                                    surveyQuestions.length >=
                                                                                                    5000
                                                                                                ) {
                                                                                                    showSaveError(
                                                                                                        "You've hit the maximum amount of test questions! If you need to add more, consider removing earlier ones.",
                                                                                                    );
                                                                                                    return;
                                                                                                }
                                                                                                setQuestionType(
                                                                                                    "ratingscale",
                                                                                                );
                                                                                            }}
                                                                                        />
                                                                                        <QuestionCard
                                                                                            questiontype="Ranking scale"
                                                                                            onClick={() => {
                                                                                                if (
                                                                                                    surveyQuestions.length >=
                                                                                                    5000
                                                                                                ) {
                                                                                                    showSaveError(
                                                                                                        "You've hit the maximum amount of test questions! If you need to add more, consider removing earlier ones.",
                                                                                                    );
                                                                                                    return;
                                                                                                }
                                                                                                setQuestionType(
                                                                                                    "rankingscale",
                                                                                                );
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                    <div className="create-test-preview-area">
                                                                                        <Text
                                                                                            type={"overline"}
                                                                                            children={"Preview"}
                                                                                        />
                                                                                        <div className="create-test-preview-area-inner questionpreviewinner">
                                                                                            choose a survey question <br /> to
                                                                                            preview
                                                                                        </div>
                                                                                        <GuideLink methodology={"survey"} />
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}

                                                                    {questionType !== "" &&
                                                                        editingSurveyIndex === null && (
                                                                            <div className="">
                                                                                <div
                                                                                    className={
                                                                                        test.language === "en"
                                                                                            ? ""
                                                                                            : "arabic_wrapper"
                                                                                    }
                                                                                >
                                                                                    <QuestionInput
                                                                                        key="add"
                                                                                        questionType={questionType}
                                                                                        cancelQuestion={
                                                                                            cancelQuestionHandler
                                                                                        }
                                                                                        ref={
                                                                                            questionInputValidateRef
                                                                                        }
                                                                                        editingQuestion={null}
                                                                                        methodology="Survey"
                                                                                        islogic={Boolean(
                                                                                            questionType,
                                                                                        )}
                                                                                        addQuestions={(question) => {
                                                                                            const next =
                                                                                                normalizeSurveyQuestionSequentialIds(
                                                                                                    [
                                                                                                        ...surveyQuestions,
                                                                                                        stripQuestionTransientIds(
                                                                                                            question,
                                                                                                        ),
                                                                                                    ],
                                                                                                );
                                                                                            setSurveyQuestions(next);
                                                                                            setQuestionType("");
                                                                                            setStepperError(false);
                                                                                            persistSurveyQuestions(next);
                                                                                        }}
                                                                                        language={test.language}
                                                                                        questionno={
                                                                                            surveyQuestions.length + 1
                                                                                        }
                                                                                        questionlist={surveyQuestions}
                                                                                        changeQuestionType={(qt) =>
                                                                                            setQuestionType(qt)
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                </>
                                                            </>
                                                        </Accordian>

                                                        <Accordian
                                                            title="Conclusion questions"
                                                            subtitle="(Optional)"
                                                            tooltip="Some cool text"
                                                            open={Boolean(accordians.conclusion)}
                                                            onChange={() => {
                                                                const next = { ...accordians };
                                                                next.conclusion = !accordians.conclusion;
                                                                setAccordians(next);
                                                            }}
                                                            questionCount={concCount}
                                                        >
                                                            <ConclusionQuestions
                                                                ref={conclusionQuestionRef}
                                                                questions={conclusionQuestions}
                                                                test={test}
                                                                setLoading={(status) => setIsSaved(status)}
                                                                changeCount={(n) => setConcCount(n)}
                                                                updateQuestions={(q) =>
                                                                    setConclusionQuestions([...q])
                                                                }
                                                            />
                                                        </Accordian>

                                                        {surveyError && (
                                                            <span
                                                                className="input_error followup-question-error"
                                                                id="survey-error"
                                                            >
                                                                {surveyError}
                                                            </span>
                                                        )}

                                                    </>
                                                )}
                                            </div>
                                            <div className="btns-wrap-bottom">
                                                <Button
                                                    onClick={() => {
                                                        navigate("/r/test/" + test_id + "/welcome");
                                                    }}
                                                    type="ghost"
                                                    label="Previous"
                                                    size="large"
                                                    iconLeft={
                                                        <Icon
                                                            value="back-arrow"
                                                            colorClass="gray-900-svg"
                                                            size="large"
                                                        />
                                                    }
                                                />
                                                <Button
                                                    onClick={surveyValidateForm}
                                                    iconRight={
                                                        <Icon
                                                            value="forward-arrow"
                                                            colorClass="gray-50-svg"
                                                        />
                                                    }
                                                    label="Next"
                                                    size="large"
                                                    state={
                                                        surveyQuestions.length === 0 ||
                                                            isSaved.status === "loading"
                                                            ? "disabled"
                                                            : ""
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="three-column-layout-right">
                                <a
                                    onClick={(e) => {
                                        e.preventDefault();

                                        setShowSupportPanel(true);
                                    }}
                                    href={process.env.REACT_APP_URL + "r/support"}
                                    target="_blank"
                                    className="support-button"
                                >
                                    <Icon
                                        value="support"
                                        colorClass="gray-50-svg"
                                        size="medium"
                                    />
                                    Support
                                </a>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </LayoutResearcher>
    );
}
